import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Task as TaskIcon,
  Error as BugIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon
} from '@mui/icons-material';

// Mock data for fields
const initialFields = [
  { id: 1, name: 'Summary', description: 'Brief description of the ticket', fieldType: 'inline-text', type: 'default' },
  { id: 2, name: 'Description', description: 'Detailed description of the ticket', fieldType: 'text-area', type: 'default' },
  { id: 3, name: 'Checklist', description: 'List of tasks to complete', fieldType: 'checkbox', type: 'default' },
  { id: 4, name: 'Reporter', description: 'Person who reported the ticket', fieldType: 'members-list-dropdown-single', type: 'default' },
  { id: 5, name: 'Assignee', description: 'Person assigned to work on the ticket', fieldType: 'members-list-dropdown-single', type: 'default' },
  { id: 6, name: 'Team', description: 'Team responsible for the ticket', fieldType: 'dropdown-multiple', type: 'default' },
  { id: 7, name: 'Label', description: 'Tags to categorize the ticket', fieldType: 'dropdown-multiple', type: 'default' },
  { id: 8, name: 'Priority', description: 'Importance level of the ticket', fieldType: 'dropdown-single', type: 'default' },
  { id: 9, name: 'Estimated Time', description: 'Time estimate for completion', fieldType: 'inline-text', type: 'default' },
  { id: 10, name: 'Due Date', description: 'Deadline for the ticket', fieldType: 'date-time', type: 'default' },
  { id: 11, name: 'Sprint', description: 'Sprint the ticket belongs to', fieldType: 'dropdown-single', type: 'default' },
  { id: 12, name: 'Epic', description: 'Epic the ticket belongs to', fieldType: 'dropdown-single', type: 'default' },
  { id: 13, name: 'Linked Tickets', description: 'Related tickets', fieldType: 'dropdown-multiple', type: 'default' },
  { id: 14, name: 'Depends On', description: 'Tickets this depends on', fieldType: 'dropdown-multiple', type: 'default' },
  { id: 15, name: 'Attachment', description: 'Files attached to the ticket', fieldType: 'files', type: 'default' },
  { id: 16, name: 'Parent', description: 'Parent ticket if this is a subtask', fieldType: 'dropdown-single', type: 'default' },
  { id: 17, name: 'Subtask', description: 'Child tickets of this ticket', fieldType: 'dropdown-multiple', type: 'default' },
  { id: 18, name: 'Created Date', description: 'When the ticket was created', fieldType: 'date-time', type: 'system' },
  { id: 19, name: 'Updated Date', description: 'When the ticket was last updated', fieldType: 'date-time', type: 'system' },
  { id: 20, name: 'Created By', description: 'Who created the ticket', fieldType: 'members-list-dropdown-single', type: 'system' },
  { id: 21, name: 'Planned Date', description: 'Planned start date', fieldType: 'date-time', type: 'custom' },
  { id: 22, name: 'Status', description: 'Current status of the ticket', fieldType: 'dropdown-single', type: 'default' },
  { id: 23, name: 'Resolution', description: 'How the ticket was resolved', fieldType: 'dropdown-single', type: 'custom' },
  { id: 24, name: 'Environment', description: 'Environment where the issue occurs', fieldType: 'dropdown-single', type: 'custom' },
  { id: 25, name: 'Severity', description: 'Impact level of the issue', fieldType: 'dropdown-single', type: 'custom' },
  { id: 26, name: 'Version', description: 'Software version affected', fieldType: 'inline-text', type: 'custom' },
  { id: 27, name: 'Component', description: 'System component affected', fieldType: 'dropdown-single', type: 'custom' },
  { id: 28, name: 'Story Points', description: 'Agile story point estimation', fieldType: 'number', type: 'custom' },
  { id: 29, name: 'Acceptance Criteria', description: 'Criteria for ticket completion', fieldType: 'text-area', type: 'custom' },
  { id: 30, name: 'Business Value', description: 'Business impact of the ticket', fieldType: 'dropdown-single', type: 'custom' }
];

// Mock data for ticket types
const initialTicketTypes = [
  { id: 'task', name: 'Task', icon: 'task', count: 20, description: 'A task that needs to be completed' },
  { id: 'bug', name: 'Bug', icon: 'bug', count: 15, description: 'A problem that needs to be fixed' },
  { id: 'story', name: 'Story', icon: 'story', count: 25, description: 'A user story or feature request' },
  { id: 'epic', name: 'Epic', icon: 'epic', count: 8, description: 'A large body of work' },
  { id: 'subtask', name: 'Subtask', icon: 'subtask', count: 12, description: 'A smaller part of a larger task' }
];

const fieldTypes = [
  { value: 'all', label: 'All Field Types' },
  { value: 'inline-text', label: 'Inline Text' },
  { value: 'text-area', label: 'Text Area' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'dropdown-single', label: 'Dropdown Single' },
  { value: 'dropdown-multiple', label: 'Dropdown Multiple' }
];

const types = [
  { value: 'all', label: 'All Types' },
  { value: 'custom', label: 'Custom' },
  { value: 'default', label: 'Default' },
  { value: 'system', label: 'System' }
];

const ticketTypeCategories = [
  { value: 'all', label: 'All Categories' },
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'design', label: 'Design' },
  { value: 'documentation', label: 'Documentation' }
];

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldTypeFilter, setFieldTypeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ticketTypeSearch, setTicketTypeSearch] = useState('');
  const [ticketTypeCategory, setTicketTypeCategory] = useState('all');
  
  // Fields management state
  const [fields, setFields] = useState(initialFields);
  const [editingField, setEditingField] = useState(null);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [fieldFormData, setFieldFormData] = useState({
    name: '',
    description: '',
    fieldType: 'inline-text',
    type: 'custom'
  });
  const [fieldError, setFieldError] = useState('');

  // Ticket types management state
  const [ticketTypes, setTicketTypes] = useState(initialTicketTypes);
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [isTicketTypeModalOpen, setIsTicketTypeModalOpen] = useState(false);
  const [ticketTypeFormData, setTicketTypeFormData] = useState({
    name: '',
    description: '',
    icon: 'task'
  });
  const [ticketTypeError, setTicketTypeError] = useState('');

  // Import state
  const [importData, setImportData] = useState('');
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [isImportFieldsModalOpen, setIsImportFieldsModalOpen] = useState(false);
  const [isImportTypesModalOpen, setIsImportTypesModalOpen] = useState(false);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFieldTypeChange = (e) => {
    setFieldTypeFilter(e.target.value);
  };

  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
  };

  const handleTicketTypeSearchChange = (e) => {
    setTicketTypeSearch(e.target.value);
  };

  const handleTicketTypeCategoryChange = (e) => {
    setTicketTypeCategory(e.target.value);
  };

  // Field management functions
  const handleEditField = (field) => {
    setEditingField(field);
    setFieldFormData({
      name: field.name,
      description: field.description,
      fieldType: field.fieldType,
      type: field.type
    });
    setIsFieldModalOpen(true);
    setFieldError('');
  };

  const handleDeleteField = (fieldId) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      setFields(fields.filter(field => field.id !== fieldId));
    }
  };

  const handleFieldFormChange = (e) => {
    const { name, value } = e.target;
    setFieldFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFieldSubmit = () => {
    if (!fieldFormData.name.trim()) {
      setFieldError('Field name is required');
      return;
    }

    if (editingField) {
      // Update existing field
      setFields(fields.map(field => 
        field.id === editingField.id 
          ? { ...field, ...fieldFormData }
          : field
      ));
    } else {
      // Add new field
      const newField = {
        id: Math.max(...fields.map(f => f.id)) + 1,
        ...fieldFormData
      };
      setFields([...fields, newField]);
    }

    handleCloseFieldModal();
  };

  const handleCloseFieldModal = () => {
    setIsFieldModalOpen(false);
    setEditingField(null);
    setFieldFormData({
      name: '',
      description: '',
      fieldType: 'inline-text',
      type: 'custom'
    });
    setFieldError('');
  };

  // Ticket type management functions
  const handleEditTicketType = (ticketType) => {
    setEditingTicketType(ticketType);
    setTicketTypeFormData({
      name: ticketType.name,
      description: ticketType.description,
      icon: ticketType.icon
    });
    setIsTicketTypeModalOpen(true);
    setTicketTypeError('');
  };

  const handleDeleteTicketType = (ticketTypeId) => {
    if (window.confirm('Are you sure you want to delete this ticket type?')) {
      setTicketTypes(ticketTypes.filter(type => type.id !== ticketTypeId));
    }
  };

  const handleTicketTypeFormChange = (e) => {
    const { name, value } = e.target;
    setTicketTypeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTicketTypeSubmit = () => {
    if (!ticketTypeFormData.name.trim()) {
      setTicketTypeError('Ticket type name is required');
      return;
    }

    if (editingTicketType) {
      // Update existing ticket type
      setTicketTypes(ticketTypes.map(type => 
        type.id === editingTicketType.id 
          ? { ...type, ...ticketTypeFormData }
          : type
      ));
    } else {
      // Add new ticket type
      const newTicketType = {
        id: ticketTypeFormData.name.toLowerCase().replace(/\s+/g, '-'),
        ...ticketTypeFormData,
        count: 0
      };
      setTicketTypes([...ticketTypes, newTicketType]);
    }

    handleCloseTicketTypeModal();
  };

  const handleCloseTicketTypeModal = () => {
    setIsTicketTypeModalOpen(false);
    setEditingTicketType(null);
    setTicketTypeFormData({
      name: '',
      description: '',
      icon: 'task'
    });
    setTicketTypeError('');
  };

  // Import functions
  const handleImportFields = () => {
    if (!importData.trim()) {
      setImportError('Please enter CSV data or upload a file');
      return;
    }

    try {
      const lines = importData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Validate required headers
      const requiredHeaders = ['name', 'description', 'fieldtype', 'type'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        setImportError(`Missing required headers: ${missingHeaders.join(', ')}`);
        return;
      }

      const newFields = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= 4 && values[0] && values[1]) {
          const newField = {
            id: Math.max(...fields.map(f => f.id)) + 1 + newFields.length,
            name: values[0],
            description: values[1],
            fieldType: values[2] || 'inline-text',
            type: values[3] || 'custom'
          };
          newFields.push(newField);
        }
      }

      if (newFields.length === 0) {
        setImportError('No valid fields found in the CSV data');
        return;
      }

      setFields([...fields, ...newFields]);
      setImportSuccess(`Successfully imported ${newFields.length} fields`);
      setImportData('');
      setTimeout(() => {
        setImportSuccess('');
        setIsImportFieldsModalOpen(false);
      }, 2000);

    } catch (error) {
      setImportError('Error parsing CSV data. Please check the format.');
    }
  };

  const handleImportTicketTypes = () => {
    if (!importData.trim()) {
      setImportError('Please enter CSV data or upload a file');
      return;
    }

    try {
      const lines = importData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Validate required headers
      const requiredHeaders = ['name', 'description', 'icon'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        setImportError(`Missing required headers: ${missingHeaders.join(', ')}`);
        return;
      }

      const newTicketTypes = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= 3 && values[0] && values[1]) {
          const newTicketType = {
            id: values[0].toLowerCase().replace(/\s+/g, '-'),
            name: values[0],
            description: values[1],
            icon: values[2] || 'task',
            count: 0
          };
          newTicketTypes.push(newTicketType);
        }
      }

      if (newTicketTypes.length === 0) {
        setImportError('No valid ticket types found in the CSV data');
        return;
      }

      setTicketTypes([...ticketTypes, ...newTicketTypes]);
      setImportSuccess(`Successfully imported ${newTicketTypes.length} ticket types`);
      setImportData('');
      setTimeout(() => {
        setImportSuccess('');
        setIsImportTypesModalOpen(false);
      }, 2000);

    } catch (error) {
      setImportError('Error parsing CSV data. Please check the format.');
    }
  };

  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImportData(e.target.result);
      setImportError('');
    };
    reader.readAsText(file);
  };

  const handleCloseImportModal = (type) => {
    if (type === 'fields') {
      setIsImportFieldsModalOpen(false);
    } else {
      setIsImportTypesModalOpen(false);
    }
    setImportData('');
    setImportError('');
    setImportSuccess('');
  };

  // Filter fields based on search and filters
  const filteredFields = fields.filter(field => {
    const matchesSearch = !searchTerm || 
      field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFieldType = fieldTypeFilter === 'all' || 
      field.fieldType === fieldTypeFilter;
    
    const matchesType = typeFilter === 'all' || 
      field.type === typeFilter;
    
    return matchesSearch && matchesFieldType && matchesType;
  });

  // Filter ticket types based on search and filters
  const filteredTicketTypes = ticketTypes.filter(ticketType => {
    const matchesSearch = !ticketTypeSearch || 
      ticketType.name.toLowerCase().includes(ticketTypeSearch.toLowerCase()) ||
      ticketType.description.toLowerCase().includes(ticketTypeSearch.toLowerCase());
    
    const matchesCategory = ticketTypeCategory === 'all' || 
      ticketType.id === ticketTypeCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getTicketTypeIcon = (iconType) => {
    switch (iconType) {
      case 'task': return <TaskIcon />;
      case 'bug': return <BugIcon />;
      case 'story': return <StarIcon />;
      case 'epic': return <CheckCircleIcon />;
      case 'subtask': return <SubdirectoryArrowRightIcon />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>


      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)} 
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Fields" />
          <Tab label="Ticket Type" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Fields Configuration
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Configure your ticket fields and their properties.
              </Typography>
              
              {/* Search and Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{ flexGrow: 1, minWidth: 200 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={fieldTypeFilter}
                    onChange={handleFieldTypeChange}
                    label="Field Type"
                  >
                    {fieldTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={handleTypeChange}
                    label="Type"
                  >
                    {types.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* Fields Table */}
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Field Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFields.map((field) => (
                      <TableRow key={field.id} hover>
                        <TableCell>{field.name}</TableCell>
                        <TableCell>{field.description}</TableCell>
                        <TableCell>{field.fieldType}</TableCell>
                        <TableCell>{field.type}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleEditField(field)}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="error"
                              onClick={() => handleDeleteField(field.id)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  sx={{ mr: 2 }}
                  onClick={() => {
                    setEditingField(null);
                    setFieldFormData({
                      name: '',
                      description: '',
                      fieldType: 'inline-text',
                      type: 'custom'
                    });
                    setIsFieldModalOpen(true);
                    setFieldError('');
                  }}
                >
                  Add Field
                </Button>
                
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setIsImportFieldsModalOpen(true);
                    setImportData('');
                    setImportError('');
                    setImportSuccess('');
                  }}
                >
                  Import Fields
                </Button>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Ticket Types Configuration
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Configure different ticket types (Task, Bug, Story, etc.) and their properties.
              </Typography>
              
              {/* Search and Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search ticket types..."
                  value={ticketTypeSearch}
                  onChange={handleTicketTypeSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{ flexGrow: 1, minWidth: 200 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={ticketTypeCategory}
                    onChange={handleTicketTypeCategoryChange}
                    label="Category"
                  >
                    {ticketTypeCategories.map(category => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* Ticket Types Table */}
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fields Count</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTicketTypes.map((ticketType) => (
                      <TableRow key={ticketType.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getTicketTypeIcon(ticketType.icon)}
                            <span>{ticketType.name}</span>
                          </Box>
                        </TableCell>
                        <TableCell>{ticketType.description}</TableCell>
                        <TableCell>{ticketType.count}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleEditTicketType(ticketType)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteTicketType(ticketType.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  sx={{ mr: 2 }}
                  onClick={() => {
                    setEditingTicketType(null);
                    setTicketTypeFormData({
                      name: '',
                      description: '',
                      icon: 'task'
                    });
                    setIsTicketTypeModalOpen(true);
                    setTicketTypeError('');
                  }}
                >
                  Add Ticket Type
                </Button>
                
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setIsImportTypesModalOpen(true);
                    setImportData('');
                    setImportError('');
                    setImportSuccess('');
                  }}
                >
                  Import Types
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Field Edit/Create Modal */}
      <Dialog 
        open={isFieldModalOpen} 
        onClose={handleCloseFieldModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingField ? 'Edit Field' : 'Create New Field'}
        </DialogTitle>
        <DialogContent>
          {fieldError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fieldError}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Field Name"
              name="name"
              value={fieldFormData.name}
              onChange={handleFieldFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={fieldFormData.description}
              onChange={handleFieldFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Field Type</InputLabel>
              <Select
                name="fieldType"
                value={fieldFormData.fieldType}
                onChange={handleFieldFormChange}
                label="Field Type"
              >
                {fieldTypes.slice(1).map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={fieldFormData.type}
                onChange={handleFieldFormChange}
                label="Type"
              >
                {types.slice(1).map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFieldModal}>Cancel</Button>
          <Button onClick={handleFieldSubmit} variant="contained">
            {editingField ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Type Edit/Create Modal */}
      <Dialog 
        open={isTicketTypeModalOpen} 
        onClose={handleCloseTicketTypeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTicketType ? 'Edit Ticket Type' : 'Create New Ticket Type'}
        </DialogTitle>
        <DialogContent>
          {ticketTypeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ticketTypeError}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Type Name"
              name="name"
              value={ticketTypeFormData.name}
              onChange={handleTicketTypeFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={ticketTypeFormData.description}
              onChange={handleTicketTypeFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Icon</InputLabel>
              <Select
                name="icon"
                value={ticketTypeFormData.icon}
                onChange={handleTicketTypeFormChange}
                label="Icon"
              >
                <MenuItem value="task">Task</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="story">Story</MenuItem>
                <MenuItem value="epic">Epic</MenuItem>
                <MenuItem value="subtask">Subtask</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTicketTypeModal}>Cancel</Button>
          <Button onClick={handleTicketTypeSubmit} variant="contained">
            {editingTicketType ? 'Update' : 'Create'}
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Import Fields Modal */}
       <Dialog 
         open={isImportFieldsModalOpen} 
         onClose={() => handleCloseImportModal('fields')}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>Import Fields</DialogTitle>
         <DialogContent>
           <Box sx={{ mb: 2 }}>
             <Typography variant="body2" color="text.secondary" gutterBottom>
               Import fields from CSV format. The CSV should have the following headers:
             </Typography>
             <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
               name, description, fieldtype, type
             </Typography>
           </Box>
           
           {importError && (
             <Alert severity="error" sx={{ mb: 2 }}>
               {importError}
             </Alert>
           )}
           
           {importSuccess && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {importSuccess}
             </Alert>
           )}

           <Box sx={{ mb: 2 }}>
             <input
               accept=".csv,.txt"
               style={{ display: 'none' }}
               id="fields-file-upload"
               type="file"
               onChange={(e) => handleFileUpload(e, 'fields')}
             />
             <label htmlFor="fields-file-upload">
               <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                 Upload CSV File
               </Button>
             </label>
             <Typography variant="caption" color="text.secondary">
               Or paste CSV data below:
             </Typography>
           </Box>

           <TextField
             fullWidth
             multiline
             rows={8}
             value={importData}
             onChange={(e) => setImportData(e.target.value)}
             placeholder="Paste CSV data here or upload a file above..."
             variant="outlined"
           />
         </DialogContent>
         <DialogActions>
           <Button onClick={() => handleCloseImportModal('fields')}>Cancel</Button>
           <Button onClick={handleImportFields} variant="contained">
             Import Fields
           </Button>
         </DialogActions>
       </Dialog>

       {/* Import Ticket Types Modal */}
       <Dialog 
         open={isImportTypesModalOpen} 
         onClose={() => handleCloseImportModal('types')}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>Import Ticket Types</DialogTitle>
         <DialogContent>
           <Box sx={{ mb: 2 }}>
             <Typography variant="body2" color="text.secondary" gutterBottom>
               Import ticket types from CSV format. The CSV should have the following headers:
             </Typography>
             <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
               name, description, icon
             </Typography>
           </Box>
           
           {importError && (
             <Alert severity="error" sx={{ mb: 2 }}>
               {importError}
             </Alert>
           )}
           
           {importSuccess && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {importSuccess}
             </Alert>
           )}

           <Box sx={{ mb: 2 }}>
             <input
               accept=".csv,.txt"
               style={{ display: 'none' }}
               id="types-file-upload"
               type="file"
               onChange={(e) => handleFileUpload(e, 'types')}
             />
             <label htmlFor="types-file-upload">
               <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                 Upload CSV File
               </Button>
             </label>
             <Typography variant="caption" color="text.secondary">
               Or paste CSV data below:
             </Typography>
           </Box>

           <TextField
             fullWidth
             multiline
             rows={8}
             value={importData}
             onChange={(e) => setImportData(e.target.value)}
             placeholder="Paste CSV data here or upload a file above..."
             variant="outlined"
           />
         </DialogContent>
         <DialogActions>
           <Button onClick={() => handleCloseImportModal('types')}>Cancel</Button>
           <Button onClick={handleImportTicketTypes} variant="contained">
             Import Types
           </Button>
         </DialogActions>
       </Dialog>
     </Container>
   );
 };

export default Settings;
